import { useState } from 'react'
import { AlertTriangle, Trash2, CheckCircle, ArrowLeft } from 'lucide-react'

export default function DeleteMyAccountPage() {
  const [step, setStep] = useState(1) // 1: warning, 2: confirmation, 3: success
  const [confirmationText, setConfirmationText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDeleteAccount = async () => {
    if (confirmationText !== 'DELETE') {
      alert('Please type "DELETE" exactly as shown to confirm')
      return
    }

    setIsDeleting(true)
    
    // Simulate API call
    setTimeout(() => {
      setIsDeleting(false)
      setStep(3)
    }, 2000)
  }

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start space-x-4">
        <AlertTriangle className="text-red-500 mt-1 flex-shrink-0" size={24} />
        <div>
          <h2 className="text-xl font-semibold text-red-800 mb-2">Delete Your Account</h2>
          <p className="text-red-700 leading-relaxed">
            We're sorry to see you go. Before you proceed, please understand what happens when you delete your account.
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 pt-6">
        <button 
          className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          onClick={() => window.history.back()}
        >
          Cancel
        </button>
        <button 
          className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
          onClick={() => setStep(2)}
        >
          Continue to Delete Account
        </button>
      </div>
    </div>
  )

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start space-x-4">
        <Trash2 className="text-red-500 mt-1 flex-shrink-0" size={24} />
        <div>
          <h2 className="text-xl font-semibold text-red-800 mb-2">Confirm Account Deletion</h2>
          <p className="text-red-700 leading-relaxed">
            This is your final chance to reconsider. Once you delete your account, all your data will be permanently removed.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <label htmlFor="confirmation" className="block text-sm font-medium text-gray-700">
          To confirm deletion, please type <strong className="text-red-600">DELETE</strong> in the box below:
        </label>
        <input
          id="confirmation"
          type="text"
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          placeholder="Type DELETE here"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-4 pt-6">
        <button 
          className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          onClick={() => setStep(1)}
        >
          Go Back
        </button>
        <button 
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            confirmationText === 'DELETE' && !isDeleting
              ? 'bg-red-600 text-white hover:bg-red-700' 
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
          onClick={handleDeleteAccount}
          disabled={confirmationText !== 'DELETE' || isDeleting}
        >
          {isDeleting ? 'Deleting Account...' : 'Delete My Account'}
        </button>
      </div>
    </div>
  )

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 flex items-start space-x-4">
        <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={24} />
        <div>
          <h2 className="text-xl font-semibold text-green-800 mb-2">Account Deleted Successfully</h2>
          <p className="text-green-700 leading-relaxed">
            Your account has been successfully deleted. All your personal data has been removed from our systems.
          </p>
        </div>
      </div>

      <div className="pt-6">
        <button 
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          onClick={() => window.location.href = '/'}
        >
          Return to Home
        </button>
      </div>
    </div>
  )

  return (
    <main className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="flex items-center mb-8">
            <button 
              onClick={() => window.history.back()}
              className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-3xl font-bold text-gray-800">Delete My Account</h1>
          </div>

          <div className="text-center mb-8">
            <img 
              src="/logo.jpeg" 
              alt="Carz Bazzar Logo" 
              width={120} 
              height={60} 
              className="mx-auto mb-4"
            />
          </div>

          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}

          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Need Help?</h3>
            <p className="text-gray-700 mb-4">If you have questions about account deletion or need assistance, please contact us:</p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700 mb-2"><strong>Email:</strong> carsbazzarmain@gmail.com</p>
              <p className="text-gray-700 mb-2"><strong>Phone:</strong> +91 9773460662</p>
              <p className="text-gray-700"><strong>Hours:</strong> Mon–Fri, 9am–6pm</p>
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}
